import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { googleAuth, requestPasswordReset } from '../../api/backend';
import './AuthModal.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Same Google OAuth client the homepage and tanks use
const GOOGLE_CLIENT_ID = '726694494962-bdh2mr4th3h1pscpo9eho50bmbro4r43.apps.googleusercontent.com';

const AuthModal = () => {
  const { authOpen, authReason, closeAuth, signin, signup, signinWithToken } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // Brief "Login... Success!" flash before the modal closes, so players get
  // clear confirmation instead of the popup just vanishing.
  const [success, setSuccess] = useState(false);
  const googleDivRef = useRef(null);

  // The 20-free-spins funnel opens straight into signup mode.
  useEffect(() => {
    if (authOpen && authReason === 'signup20') {
      setMode('signup');
    }
  }, [authOpen, authReason]);

  // Google credential -> backend verify -> adopt the cross-app session token
  const handleGoogleCredential = useCallback(async (response) => {
    setError(null);
    setSubmitting(true);
    try {
      const data = await googleAuth(response.credential, mode);
      await signinWithToken(data.crossAppToken, data.email);
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setPassword('');
        setEmail('');
        setError(null);
        setInfo(null);
        setSubmitting(false);
        closeAuth();
      }, 2900);
    } catch (err) {
      setError(err.message || 'Google sign-in failed. Please try again.');
      setSubmitting(false);
    }
  }, [mode, signinWithToken, closeAuth]);

  // Load Google Identity Services (once) and render the official button
  useEffect(() => {
    if (!authOpen) {
      return undefined;
    }
    let cancelled = false;

    const renderButton = () => {
      if (cancelled || !window.google?.accounts?.id || !googleDivRef.current) {
        return;
      }
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential
      });
      googleDivRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleDivRef.current, {
        theme: 'filled_blue',
        size: 'large',
        text: mode === 'login' ? 'signin_with' : 'continue_with',
        shape: 'pill',
        width: 240
      });
    };

    if (window.google?.accounts?.id) {
      renderButton();
    } else {
      let script = document.getElementById('gsi-client-script');
      if (!script) {
        script = document.createElement('script');
        script.id = 'gsi-client-script';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderButton, { once: true });
    }

    return () => {
      cancelled = true;
    };
  }, [authOpen, mode, handleGoogleCredential]);

  if (!authOpen) {
    return null;
  }

  const reset = () => {
    setPassword('');
    setError(null);
    setInfo(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    setEmail('');
    closeAuth();
  };

  const switchMode = (next) => {
    setMode(next);
    setError(null);
    setInfo(null);
  };

  const handleForgotPassword = async () => {
    setError(null);
    setInfo(null);
    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Enter your email above first, then tap Forgot password.');
      return;
    }
    try {
      await requestPasswordReset(email.trim());
      setInfo('Reset link sent — check your email (and spam folder). The link is valid for 1 hour.');
    } catch (err) {
      setError(err.message || 'Could not send the reset link. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setInfo(null);

    if (!EMAIL_REGEX.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'signup') {
        await signup(email.trim(), password);
      } else {
        await signin(email.trim(), password);
      }
      setSuccess(true);
      // Word (0s) -> three dots (~0.4s apart) -> "Success!" at ~1.6s, then
      // hold it on screen a beat before closing.
      setTimeout(() => {
        setSuccess(false);
        handleClose();
      }, 2900);
    } catch (err) {
      // This email already has a Satoshi Advance account (homepage or another
      // mini game): flip to login with that password, or offer a reset.
      if (err.code === 'account_exists') {
        setMode('login');
        setPassword('');
        setInfo(
          'This email already has a Satoshi Advance account — from the homepage or ' +
          'another mini game. Log in with that password, or tap "Forgot password?" to reset it.'
        );
        setSubmitting(false);
        return;
      }
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const title = mode === 'signup' ? 'Create Account' : 'Log In';

  return (
    <div className="auth-modal">
      <div className="auth-modal__overlay" onClick={handleClose} />
      <div className="auth-modal__card" role="dialog" aria-modal="true" aria-label={title}>
        {success ? (
          <div className="auth-modal__success">
            {/* Retro loading sequence: dots land one by one, then Success! */}
            {mode === 'login' ? 'Login' : 'Signed Up'}
            <span className="auth-modal__success-dot">.</span>
            <span className="auth-modal__success-dot">.</span>
            <span className="auth-modal__success-dot">.</span>
            <span className="auth-modal__success-word"> Success!</span>
          </div>
        ) : (
          <>
        <button
          type="button"
          className="auth-modal__close"
          onClick={handleClose}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="auth-modal__title">{title}</h2>

        {authReason === 'purchase' && (
          <p className="auth-modal__reason">Log in or sign up to buy spins and save your balance.</p>
        )}
        {authReason === 'dashboard' && (
          <p className="auth-modal__reason">Log in to see your balance and purchase history.</p>
        )}
        {authReason === 'signup20' && (
          <p className="auth-modal__reason">
            Sign up with your email and get <b>20 FREE SPINS</b> — plus your scores and
            high score saved to your account!
          </p>
        )}

        {/* name= and autoComplete hints are what browser password managers
            key on to offer save/autofill — keep them intact. */}
        <form className="auth-modal__form" method="post" onSubmit={handleSubmit}>
          <label className="auth-modal__label" htmlFor="auth-email">Email</label>
          <input
            id="auth-email"
            name="email"
            className="auth-modal__input"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />

          <label className="auth-modal__label" htmlFor="auth-password">Password</label>
          <div className="auth-modal__password-wrap">
            <input
              id="auth-password"
              name="password"
              className="auth-modal__input"
              type={showPassword ? 'text' : 'password'}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
            />
            <button
              type="button"
              className="auth-modal__eye"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((v) => !v)}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 4.5C7 4.5 2.7 7.6 1 12c1.7 4.4 6 7.5 11 7.5s9.3-3.1 11-7.5C21.3 7.6 17 4.5 12 4.5zm0 12.5c-2.8 0-5-2.2-5-5s2.2-5 5-5 5 2.2 5 5-2.2 5-5 5zm0-8c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3-1.3-3-3-3z" />
                {showPassword && (
                  <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2.4" />
                )}
              </svg>
            </button>
          </div>

          {error && <div className="auth-modal__error">{error}</div>}
          {info && <div className="auth-modal__info">{info}</div>}

          <button type="submit" className="auth-modal__submit" disabled={submitting}>
            {submitting ? 'Please wait…' : mode === 'signup' ? 'Sign Up' : 'Log In'}
          </button>

          {mode === 'login' && (
            <button
              type="button"
              className="auth-modal__forgot"
              onClick={handleForgotPassword}
            >
              Forgot password?
            </button>
          )}
        </form>

        {/* Google sign-in — same account system, one tap */}
        <div className="auth-modal__divider">— or —</div>
        <div className="auth-modal__google" ref={googleDivRef} />

        <div className="auth-modal__switch">
          {mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('login')}>Log in</button>
            </>
          ) : (
            <>
              New here?{' '}
              <button type="button" onClick={() => switchMode('signup')}>Create an account</button>
            </>
          )}
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
